import { useEffect, useRef, useState } from 'react';

export const REMOTE_SEARCH_DEBOUNCE_MS = 500;

interface UseDebouncedRemoteSearchOptions<TResult> {
  enabled?: boolean;
  query: string;
  search: (query: string) => Promise<TResult>;
}

interface UseDebouncedRemoteSearchResult<TResult> {
  data: TResult | null;
  isLoading: boolean;
  isSearching: boolean;
  normalizedQuery: string;
}

export function useDebouncedRemoteSearch<TResult>({
  enabled = true,
  query,
  search,
}: UseDebouncedRemoteSearchOptions<TResult>): UseDebouncedRemoteSearchResult<TResult> {
  const [data, setData] = useState<TResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const normalizedQuery = query.trim();
  const isSearching = enabled && normalizedQuery.length > 0;

  useEffect(() => {
    if (!isSearching) {
      requestIdRef.current += 1;
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      setData(null);
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setData(null);
    setIsLoading(true);

    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      search(normalizedQuery)
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setData(result);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        });
    }, REMOTE_SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, [isSearching, normalizedQuery, search]);

  return {
    data,
    isLoading,
    isSearching,
    normalizedQuery,
  };
}
